import styled from 'styled-components';
import classNames from 'classnames';
import Title, { TitleProps } from '../title';
import { SpacingProps } from '../box';
import { spacingNames } from '../../style/spacing';
import { withSpacing } from '../../utils';
import fonts from '../../style/fonts';
import type { FontFamily } from '../../style/types';
import { sizes } from './style';

export interface TextProps extends Omit<TitleProps, 'componentClass' | 'size' | 'htmlFor'>, SpacingProps {
  size?: keyof typeof sizes;
  componentClass?: 'label' | 'p' | 'span';
  fontFamily?: FontFamily;
}

const Text = styled(({ className, weight = 400, ...rest }: TextProps) => {
  // dont pass size props to text, it will handle its own via css
  const propsToPass = { ...rest, size: undefined };

  return (
    <Title
      className={classNames('box-text', className)}
      marginBottom={'none'}
      weight={weight}
      display={'block'}
      componentClass={'span'}
      {...propsToPass}
    />
  );
}).withConfig({
  shouldForwardProp: prop => ![...spacingNames, 'fontFamily'].includes(prop),
})`
  ${withSpacing}

  ${props => sizes[props.size || 'lg']};
  font-family: ${props => fonts[props.fontFamily || props.theme?.textFontFamily || 'roboto']};
`;

Text.displayName = 'Text';

const StyledHtmlText = styled(Text)`
  ul,
  ol {
    margin: 0;
    padding-left: 1em;
  }

  strong {
    font-weight: 700;
  }

  li {
    margin: 0;
    padding-left: 0.25em;
    line-height: 1.5;
  }
`;

StyledHtmlText.displayName = 'StyledHtmlText';

const HtmlText = ({ children, ...rest }: TextProps) => {
  const htmlContent = children != null ? String(children) : '';
  return (
    <StyledHtmlText {...rest}>
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    </StyledHtmlText>
  );
};

HtmlText.displayName = 'HtmlText';

export interface LabelProps extends TextProps {
  htmlFor?: TitleProps['htmlFor'];
}

const Label = (props: LabelProps) => <Text weight={500} componentClass={'label'} {...props} />;

const Paragraph = ({ ...rest }: TextProps) => <Text marginBottom={'xs'} componentClass={'p'} {...rest} />;

export { Paragraph, Label, HtmlText };

export default Text;
